import React, {
  useCallback, useEffect, useState, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { darkModeAware, withStyles } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import {
  SquareNextIcon,
  CheckmarkSquareNextIcon,
  CircleNextIcon,
  CheckmarkMinusSquareAshIcon,
  CircleSelectedNextIcon
} from '@glu/icons-react';
import LabelText from '../LabelText/LabelText';
import styles from './CheckboxRadio.styles';
import Error from '../Error/Error';

let uniqueId = 0;

const propTypes = {
  /** form field id */
  htmlId: PropTypes.string,
  /** element value */
  htmlValue: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.string, PropTypes.number
  ]),
  /** form field name */
  name: PropTypes.string.isRequired,
  /** Determines if type is "checkbox" or "radio" */
  type: PropTypes.string,
  /** text label for checkbox */
  labelText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]).isRequired,
  /** value of the named element provided by form context */
  value: PropTypes.oneOfType([
    PropTypes.bool, PropTypes.string, PropTypes.number
  ]),
  /** function to call when checkbox changes */
  onChange: PropTypes.func.isRequired,
  /** function to call when checkbox changes */
  onKeyDown: PropTypes.func,
  /** JSS classes for checkbox styling (empty object with BareCheckbox) */
  classes: PropTypes.objectOf(PropTypes.string),
  /** Determines whether checkbox is checked */
  checked: PropTypes.bool,
  /** Classname to apply to container label element */
  className: PropTypes.string,
  /** Classname to apply to checkbox label span */
  labelClass: PropTypes.string,
  /** Classname to apply to checkbox input */
  inputClass: PropTypes.string,
  /** Determines if label should be screen reader only */
  screenReaderLabel: PropTypes.bool,
  /** Determines if inputs should be oriented inline or block */
  inline: PropTypes.bool,
  /** Render checkbox in an indeterminate state */
  indeterminate: PropTypes.bool,
  /** Theme used for styling */
  theme: PropTypes.objectOf(PropTypes.any),
  /** dark mode boolean */
  dark: PropTypes.bool,
  /** The error to display */
  error: PropTypes.string,
  /** Full errors object. */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** Properties to pass to the label tag */
  labelTagProps: PropTypes.shape({})
};

const defaultProps = {
  checked: false,
  classes: {},
  className: '',
  dark: false,
  htmlId: undefined,
  htmlValue: true,
  inline: false,
  error: '',
  errors: undefined,
  inputClass: '',
  indeterminate: false,
  labelClass: '',
  onKeyDown: undefined,
  screenReaderLabel: false,
  theme: {},
  type: 'checkbox',
  value: false,
  labelTagProps: {}
};

const CheckboxRadio = ({
  htmlId,
  type,
  checked,
  classes,
  labelTagProps,
  labelText,
  onChange,
  onKeyDown,
  className,
  labelClass,
  inputClass,
  screenReaderLabel,
  inline,
  error,
  errors,
  name,
  indeterminate,
  theme,
  dark,
  value: fieldValue,
  htmlValue,
  ...props
}) => {
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `checkbox-radio-${uniqueId++}`, [htmlId]);
  const hasRootClass = useMemo(() => !!classes.root, [classes]);
  const [isSelected, setIsSelected] = useState(checked ? true : htmlValue === fieldValue);

  const errorMessage = error || errors?.[0];

  const getIcon = useCallback(() => {
    if (type === 'checkbox') {
      if (indeterminate) {
        return <CheckmarkMinusSquareAshIcon />;
      }
      if (isSelected) {
        return <CheckmarkSquareNextIcon />;
      }
      return <SquareNextIcon />;
    }
    if (type === 'radio' && isSelected) {
      return <CircleSelectedNextIcon />;
    }
    return <CircleNextIcon />;
  }, [type, isSelected, indeterminate]);

  useEffect(() => {
    setIsSelected(checked ? true : htmlValue === fieldValue);
  }, [checked, fieldValue, htmlValue, isSelected]);

  const handleChange = ({ target: { checked: newChecked } }) => {
    setIsSelected(newChecked);
    onChange(name, newChecked ? htmlValue : newChecked);
  };

  return (
    <label className={`${classes.root} ${className}`} htmlFor={id} {...labelTagProps}>
      <input
        id={id}
        ref={el => el && (el.indeterminate = indeterminate)} // eslint-disable-line
        type={type}
        checked={isSelected}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        className={inputClass}
        value={htmlValue}
        name={name}
        {...props}
      />

      {hasRootClass && getIcon()}

      <LabelText
        label={labelText}
        className={labelClass}
        screenReaderLabel={screenReaderLabel}
        hasError={!!errorMessage}
        checkboxLabel
        dark={dark}
      />
      <div className={classes.helpWrapper}>
        <Error error={errorMessage} dark={dark} />
      </div>

    </label>
  );
};

CheckboxRadio.propTypes = propTypes;
CheckboxRadio.defaultProps = defaultProps;

const StyledCheckboxRadio = darkModeAware(withStyles(styles)(CheckboxRadio));
const WithInputCheckboxRadio = withInputAPI(StyledCheckboxRadio);
WithInputCheckboxRadio.displayName = 'CheckboxRadio';
export const BareCheckboxRadio = CheckboxRadio;
export default WithInputCheckboxRadio;
export const ValidatedCheckboxRadio = WithInputCheckboxRadio;
